import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import Dialog from "../../../components/ui/Dialog";
import EmployeePicker from "../../../components/ui/EmployeePicker";
import PickerChips from "../../../components/ui/PickerChips";
import { useAssignParticipants } from "../../../api/queries/learningQueries";
import {
  useBranchesByCompany,
  useDepartments,
  useJobTitles,
} from "../../../api/queries/organizationQueries";
import useCurrentUser from "../../../hooks/useCurrentUser";
import { getApiErrorMessage } from "../../../utils/apiError";
import { employeeName } from "../../../utils/text";

const EMPTY = [];

/**
 * Assign employees to a seminar: pick people, and/or everyone in some
 * branches, departments or job titles. The server reports who was added,
 * who already was, and who isn't eligible.
 */
const AssignParticipantsDialog = ({ seminar, onClose }) => {
  const user = useCurrentUser();
  const [employees, setEmployees] = useState([]);
  const [branchIds, setBranchIds] = useState([]);
  const [departmentIds, setDepartmentIds] = useState([]);
  const [jobTitleIds, setJobTitleIds] = useState([]);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const assign = useAssignParticipants();

  const { data: branches = EMPTY } = useBranchesByCompany(user.companyId);
  const { data: departments = EMPTY } = useDepartments(user.companyId);
  const { data: jobTitles = EMPTY } = useJobTitles();
  const branchOptions = useMemo(
    () => branches.map((b) => ({ value: b.id, label: b.branchName })),
    [branches],
  );
  const departmentOptions = useMemo(
    () => departments.map((d) => ({ value: d.id, label: d.departmentName })),
    [departments],
  );
  const jobTitleOptions = useMemo(
    () => jobTitles.map((j) => ({ value: j.id, label: j.title || j.name })),
    [jobTitles],
  );
  const assignedIds = (seminar.participants || []).map((p) => p.employeeId);

  const nothing =
    !employees.length &&
    !branchIds.length &&
    !departmentIds.length &&
    !jobTitleIds.length;

  const submit = () => {
    setError(null);
    assign.mutate(
      {
        id: seminar.id,
        employeeIds: employees.length ? employees.map((e) => e.id) : undefined,
        branchIds: branchIds.length ? branchIds : undefined,
        departmentIds: departmentIds.length ? departmentIds : undefined,
        jobTitleIds: jobTitleIds.length ? jobTitleIds : undefined,
      },
      {
        onSuccess: (res) => {
          setResult(res);
          toast.success(`${res?.added ?? 0} participant(s) added.`);
        },
        onError: (err) =>
          setError(getApiErrorMessage(err, "Could not assign participants.")),
      },
    );
  };

  return (
    <Dialog
      title="Assign participants"
      subtitle={seminar.title}
      size="lg"
      onClose={onClose}
      busy={assign.isPending}
      footer={
        result ? (
          <button
            type="button"
            className="btn btn-primary profile-btn"
            onClick={onClose}
          >
            Done
          </button>
        ) : (
          <>
            <button
              type="button"
              className="btn btn-outline-secondary profile-btn"
              onClick={onClose}
              disabled={assign.isPending}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary profile-btn"
              onClick={submit}
              disabled={assign.isPending || nothing}
            >
              {assign.isPending && (
                <span
                  className="spinner-border spinner-border-sm"
                  aria-hidden="true"
                ></span>
              )}
              Assign
            </button>
          </>
        )
      }
    >
      {error && <div className="alert alert-danger fs-085 py-2">{error}</div>}
      {result ? (
        <div>
          <div className="learn-result">
            <div>
              <span className="approval-meta-label">Added</span>
              <strong className="text-success">{result.added ?? 0}</strong>
            </div>
            <div>
              <span className="approval-meta-label">Already assigned</span>
              <strong>{result.alreadyAssigned ?? 0}</strong>
            </div>
            <div>
              <span className="approval-meta-label">Not eligible</span>
              <strong
                className={
                  (result.notEligible || []).length ? "text-danger" : ""
                }
              >
                {(result.notEligible || []).length}
              </strong>
            </div>
          </div>
          {(result.notEligible || []).length > 0 && (
            <ul className="fs-080 mt-2 mb-0 ps-3">
              {result.notEligible.slice(0, 50).map((n, i) => (
                <li key={i}>
                  {typeof n === "string"
                    ? n
                    : `${n.name || n.employeeNumber || n.employeeId}: ${n.reason || "not eligible"}`}
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <div className="d-flex flex-column gap-3">
          <div>
            <label htmlFor="assign-employee" className="profile-label">
              Employees
            </label>
            <EmployeePicker
              id="assign-employee"
              companyId={user.companyId}
              value={null}
              onChange={(emp) =>
                emp &&
                !employees.some((e) => e.id === emp.id) &&
                setEmployees((list) => [...list, emp])
              }
              excludeId={[...assignedIds, ...employees.map((e) => e.id)]}
              placeholder="Search and add employees"
            />
            {employees.length > 0 && (
              <div className="d-flex flex-wrap gap-1 mt-2">
                {employees.map((e) => (
                  <span key={e.id} className="chip">
                    {employeeName(e)}
                    <button
                      type="button"
                      className="chip-remove"
                      onClick={() =>
                        setEmployees((list) =>
                          list.filter((x) => x.id !== e.id),
                        )
                      }
                      aria-label={`Remove ${employeeName(e)}`}
                    >
                      <i className="bi bi-x"></i>
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="text-slate-500 fs-075 fw-semibold text-uppercase">
            or everyone in…
          </div>
          <div className="row g-3">
            <div className="col-md-4">
              <label htmlFor="assign-branches" className="profile-label">
                Branches
              </label>
              <PickerChips
                id="assign-branches"
                options={branchOptions}
                value={branchIds}
                onChange={setBranchIds}
                placeholder="Add branch…"
                emptyText="None"
              />
            </div>
            <div className="col-md-4">
              <label htmlFor="assign-departments" className="profile-label">
                Departments
              </label>
              <PickerChips
                id="assign-departments"
                options={departmentOptions}
                value={departmentIds}
                onChange={setDepartmentIds}
                placeholder="Add department…"
                emptyText="None"
              />
            </div>
            <div className="col-md-4">
              <label htmlFor="assign-jobtitles" className="profile-label">
                Job titles
              </label>
              <PickerChips
                id="assign-jobtitles"
                options={jobTitleOptions}
                value={jobTitleIds}
                onChange={setJobTitleIds}
                placeholder="Add job title…"
                emptyText="None"
              />
            </div>
          </div>
          {seminar.capacity ? (
            <p className="fs-075 text-slate-500 mb-0">
              Capacity {seminar.capacity}; {seminar.seatsLeft ?? "?"} seat(s)
              left.
            </p>
          ) : null}
        </div>
      )}
    </Dialog>
  );
};

export default AssignParticipantsDialog;
